import * as React from "react";
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {FeaturedContent, MediaItem} from "../../../store/api/types";
import {useBreakpoint} from "../../../hooks/useBreakpoint";
import styles from './MediaStrip.module.scss';
import {MediaCard} from "../mediaItem/MediaCard";

export interface MediaStripProps {
    featuredContent: Pick<FeaturedContent, 'name' | 'items'>;
    onItemClick?: (item: MediaItem) => void;
}

const DRAG_THRESHOLD_PX = 5;

export const MediaStrip: React.FC<MediaStripProps> = ({featuredContent, onItemClick}) => {
    const breakpoint = useBreakpoint();
    const isDesktop = breakpoint === 'desktop';
    const listRef = React.useRef<HTMLDivElement>(null);
    const dragRef = React.useRef({active: false, startX: 0, startScrollLeft: 0, dragged: 0});
    const [canScrollLeft, setCanScrollLeft] = React.useState(false);
    const [canScrollRight, setCanScrollRight] = React.useState(false);

    React.useEffect(() => {
        const el = listRef.current;
        if (!el) return;

        const update = () => {
            const maxScroll = el.scrollWidth - el.clientWidth;
            setCanScrollLeft(el.scrollLeft > 4);
            setCanScrollRight(el.scrollLeft < maxScroll - 4);
        };

        update();
        el.addEventListener('scroll', update, {passive: true});
        const ro = new ResizeObserver(update);
        ro.observe(el);

        return () => {
            el.removeEventListener('scroll', update);
            ro.disconnect();
        };
    }, [featuredContent.items.length]);

    React.useEffect(() => {
        if (!isDesktop) return;
        const el = listRef.current;
        if (!el) return;

        const onMouseDown = (e: MouseEvent) => {
            if (e.button !== 0) return;
            dragRef.current = {
                active: true,
                startX: e.clientX,
                startScrollLeft: el.scrollLeft,
                dragged: 0,
            };
            el.classList.add(styles.mediaStrip__listDragging);
            e.preventDefault();
        };

        const onMouseMove = (e: MouseEvent) => {
            if (!dragRef.current.active) return;
            const dx = e.clientX - dragRef.current.startX;
            const absDx = Math.abs(dx);
            if (absDx > dragRef.current.dragged) dragRef.current.dragged = absDx;
            el.scrollLeft = dragRef.current.startScrollLeft - dx;
        };

        const onMouseUp = () => {
            if (!dragRef.current.active) return;
            dragRef.current.active = false;
            el.classList.remove(styles.mediaStrip__listDragging);
        };

        const onClickCapture = (e: MouseEvent) => {
            if (dragRef.current.dragged > DRAG_THRESHOLD_PX) {
                e.stopPropagation();
                e.preventDefault();
            }
            dragRef.current.dragged = 0;
        };

        el.addEventListener('mousedown', onMouseDown);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        el.addEventListener('click', onClickCapture, true);

        return () => {
            el.removeEventListener('mousedown', onMouseDown);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
            el.removeEventListener('click', onClickCapture, true);
            el.classList.remove(styles.mediaStrip__listDragging);
        };
    }, [isDesktop]);

    const scrollByPage = (dir: -1 | 1) => {
        const el = listRef.current;
        if (!el) return;
        el.scrollBy({left: dir * el.clientWidth * 0.85, behavior: 'smooth'});
    };

    return (
        <div className={styles.mediaStrip__container}>
            <div className={styles.mediaStrip__header}>
                <div className={styles.mediaStrip__title}>{featuredContent.name}</div>
                {isDesktop && (
                    <div className={styles.mediaStrip__arrows}>
                        <IconButton
                            className={styles.mediaStrip__arrowBtn}
                            onClick={() => scrollByPage(-1)}
                            disabled={!canScrollLeft}
                            aria-label={`Scroll ${featuredContent.name} left`}
                        >
                            <ChevronLeftIcon/>
                        </IconButton>
                        <IconButton
                            className={styles.mediaStrip__arrowBtn}
                            onClick={() => scrollByPage(1)}
                            disabled={!canScrollRight}
                            aria-label={`Scroll ${featuredContent.name} right`}
                        >
                            <ChevronRightIcon/>
                        </IconButton>
                    </div>
                )}
            </div>
            <div className={styles.mediaStrip__list} ref={listRef}>
                {featuredContent.items.map(item =>
                    <div key={item.id} className={styles.mediaStrip__item}>
                        <MediaCard item={item} onClick={onItemClick}/>
                    </div>
                )}
            </div>
        </div>
    );
}
